import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook to trap focus within a modal or dialog
 * Ensures keyboard navigation stays within the container
 * Automatically restores focus when container is deactivated
 *
 * @param isActive - Whether the focus trap is active
 * @param options - Configuration options
 * @returns Ref to attach to the container element
 *
 * @example
 * const Modal = ({ isOpen, onClose }) => {
 *   const trapRef = useFocusTrap(isOpen);
 *
 *   return isOpen ? (
 *     <div ref={trapRef} role="dialog">
 *       <button onClick={onClose}>Close</button>
 *       <input />
 *     </div>
 *   ) : null;
 * };
 */
export const useFocusTrap = <T extends HTMLElement = HTMLDivElement>(
  isActive: boolean,
  options: {
    /** Element to focus when trap activates. Default: first focusable element */
    initialFocus?: RefObject<HTMLElement>;
    /** Whether to restore focus on deactivation. Default: true */
    restoreFocus?: boolean;
    /** Allow clicks outside to break the trap. Default: false */
    clickOutsideDeactivates?: boolean;
    /** Delay in ms before auto-focusing. Default: 100 */
    autoFocusDelay?: number;
  } = {}
): RefObject<T> => {
  const {
    initialFocus,
    restoreFocus = true,
    clickOutsideDeactivates = false,
    autoFocusDelay = 500,
  } = options;

  const containerRef = useRef<T>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const wasKeyboardNavigation = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isActive) return;

    // Wait for the container to be available in the DOM
    const checkContainer = () => {
      const container = containerRef.current;
      if (!container) {
        return false;
      }
      return true;
    };

    // Retry logic to wait for container
    let retries = 0;
    const maxRetries = 10;
    const retryInterval = 50;

    const setupFocusTrap = () => {
      if (!checkContainer()) {
        if (retries < maxRetries) {
          retries++;
          setTimeout(setupFocusTrap, retryInterval);
        }
        return;
      }

      const container = containerRef.current!;

      // Store the previously focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Check if the user was navigating via keyboard
      try {
        wasKeyboardNavigation.current = !!previousActiveElement.current?.matches(':focus-visible');
      } catch (e) {
        wasKeyboardNavigation.current = false;
      }

      // Get all focusable elements within the container
      const getFocusableElements = (): HTMLElement[] => {
        const selector = [
          'a[href]',
          'area[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'button:not([disabled])',
          'iframe',
          'object',
          'embed',
          '[contenteditable]',
          '[tabindex]:not([tabindex^="-"])',
        ].join(', ');

        return Array.from(container.querySelectorAll<HTMLElement>(selector));
      };

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) {
        // Retry if no focusable elements found yet
        if (retries < maxRetries) {
          retries++;
          setTimeout(setupFocusTrap, retryInterval);
        }
        return;
      }

      const firstElement = focusableElements[0];

      // Set initial focus with delay
      const focusTimeout = setTimeout(() => {
        if (initialFocus?.current) {
          initialFocus.current.focus();
        } else {
          firstElement?.focus();
        }
      }, autoFocusDelay);

      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        const currentFocusableElements = getFocusableElements();

        if (currentFocusableElements.length === 0) return;

        const currentFirstElement = currentFocusableElements[0];
        const currentLastElement = currentFocusableElements[currentFocusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab (backwards)
          if (document.activeElement === currentFirstElement) {
            event.preventDefault();
            currentLastElement?.focus();
          }
        } else {
          // Tab (forwards)
          if (document.activeElement === currentLastElement) {
            event.preventDefault();
            currentFirstElement?.focus();
          }
        }
      };

      const handleClickOutside = (event: MouseEvent) => {
        if (!clickOutsideDeactivates) return;
        if (!container.contains(event.target as Node)) {
          // Click was outside the container
          previousActiveElement.current?.focus();
        }
      };

      // Add event listeners
      document.addEventListener('keydown', handleTabKey);
      if (clickOutsideDeactivates) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      // Cleanup
      cleanupRef.current = () => {
        clearTimeout(focusTimeout);
        document.removeEventListener('keydown', handleTabKey);
        if (clickOutsideDeactivates) {
          document.removeEventListener('mousedown', handleClickOutside);
        }

        // Restore focus to the previously focused element only if user was navigating via keyboard
        if (restoreFocus && previousActiveElement.current && wasKeyboardNavigation.current) {
          // Use setTimeout to ensure the element is visible before focusing
          setTimeout(() => {
            previousActiveElement.current?.focus();
          }, 0);
        }
      };
    };

    // Start setup
    setupFocusTrap();

    // Cleanup
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [isActive, initialFocus, restoreFocus, clickOutsideDeactivates, autoFocusDelay]);

  return containerRef;
};

export default useFocusTrap;
